/**
 * @author Housefolios
 * @createTime 2025-01-27 10:00:00
 * @modifiedBy AI Assistant
 * @modifiedTime 2025-01-27 10:00:00
 * @description Utility functions for strategy pages
 */

import {
  map,
  filter,
  maxBy,
  meanBy,
  minBy,
  isEmpty,
} from '@/utils/lodash'

/**
 * Create currency formatter
 * @returns {Intl.NumberFormat} Currency formatter
 */
export const createCurrencyFormat = () => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

/**
 * Create percentage formatter
 * @returns {Intl.NumberFormat} Percentage formatter
 */
export const createPercentageFormat = () => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
  })
}

/**
 * Calculate comparable data
 * @param {Object} props - Props object
 * @param {Array} props.compValues - Comparable values
 * @param {Object} props.lowestAverage - Lowest average data
 * @returns {Array} Comparable data array
 */
export const calculateCompData = ({ compValues, lowestAverage  }) => {
  return [
    {
      label: 'Avg. Adjusted Value',
      tourStep: 'comparableProperties-step-3',
      value: isEmpty(compValues)
        ? 0
        : meanBy(
            filter(compValues, 'propertyPrice'),
            'propertyPrice',
          ),
    },
    {
      range: true,
      label: 'Adjusted Range',
      tourStep: 'comparableProperties-step-4',
      value1: isEmpty(compValues)
        ? 0
        : minBy(compValues, 'propertyPrice').propertyPrice / 1000,
      value2: isEmpty(compValues)
        ? 0
        : maxBy(compValues, 'propertyPrice').propertyPrice / 1000,
    },
    {
      label: 'Avg Adjusted Price/sqft',
      tourStep: 'comparableProperties-step-5',
      value: isEmpty(compValues)
        ? 0
        : meanBy(
            filter(compValues, 'propertyPerSqft'),
            'propertyPerSqft',
          ),
    },
    {
      label: 'Lowest 3 comp avg',
      tourStep: 'comparableProperties-step-6',
      value: lowestAverage.afterRepairValue,
    },
  ]
}

/**
 * Create chart colors array
 * @returns {Array} Array of chart colors
 */
export const createChartColors = () => {
  return [
    '#bebebd',
    '#008ffb',
    '#ff6efb',
    '#69A251',
    '#38CDC3',
    '#e3e44f',
    '#feb019',
    '#45818E',
    '#d26767',
  ]
}

/**
 * Create horizontal bar chart options (cashflow / resale summaries).
 * Category names sit above each bar so the plot can use full width.
 * @param {Object} currencyFormat - Currency formatter
 * @param {string[]} categories - Bar labels
 * @param {string[]} [tooltipLabels=categories]
 * @returns {Object} Chart options
 */
export const createHorizontalBarChartOptions = (
  currencyFormat,
  categories,
  tooltipLabels = categories,
) => {
  const labelColor = '#3b3e66'
  const labelColors = (categories || []).map(() => labelColor)
  return {
    chart: {
      toolbar: {
        show: false,
      },
      parentHeightOffset: 0,
      offsetX: 0,
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    colors: ['#1CC943', '#F83244', '#04A3E3'],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      offsetY: -16,
      offsetX: 4,
      formatter: (_val, opts) =>
        categories[opts?.dataPointIndex] || '',
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: labelColors,
      },
      background: {
        enabled: false,
      },
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        distributed: true,
        horizontal: true,
        barHeight: '32%',
        dataLabels: {
          position: 'bottom',
          hideOverflowingLabels: false,
        },
      },
    },
    legend: {
      show: false,
    },
    grid: {
      padding: {
        left: 4,
        right: 12,
        top: 18,
        bottom: 4,
      },
    },
    tooltip: {
      theme: 'dark',
      shared: false,
      intersect: true,
      followCursor: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        return (
          '<div class="arrow_box">' +
          '<span>' +
          tooltipLabels[dataPointIndex] +
          ': ' +
          currencyFormat.format(series[seriesIndex][dataPointIndex]) +
          '</span>' +
          '</div>'
        )
      },
    },
    xaxis: {
      categories,
      tickAmount: 4,
      labels: {
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        formatter: (val) => {
          const n = Number(val)
          if (!Number.isFinite(n)) return ''
          if (n === 0) return '$0'
          const abs = Math.abs(n)
          const sign = n < 0 ? '-' : ''
          if (abs >= 1000) {
            const thousands = abs / 1000
            const rounded =
              thousands >= 100
                ? Math.round(thousands)
                : Math.round(thousands * 10) / 10
            const body = Number.isInteger(rounded)
              ? String(rounded)
              : rounded.toFixed(1)
            return `${sign}$${body}k`
          }
          return `${sign}$${Math.round(abs)}`
        },
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
  }
}

/**
 * Create basic chart options
 * @param {Object} currencyFormat - Currency formatter
 * @returns {Object} Chart options
 */
export const createBasicChartOptions = (currencyFormat) => {
  return createHorizontalBarChartOptions(
    currencyFormat,
    ['Resale', 'Expenses', 'Net Profit'],
    ['Resale Price', 'Expenses', 'Net Profit'],
  )
}

/**
 * Monthly cashflow bar chart (Income / Expenses / Cashflow).
 * @param {Object} currencyFormat - Currency formatter
 * @returns {Object} Chart options
 */
export const createCashflowChartOptions = (currencyFormat) => {
  return createHorizontalBarChartOptions(currencyFormat, [
    'Income',
    'Expenses',
    'Cashflow',
  ])
}

/**
 * Create donut chart options
 * @param {Object} currencyFormat - Currency formatter
 * @param {Array} labels - Chart labels
 * @param {Array} colors - Chart colors
 * @returns {Object} Donut chart options
 */
export const createDonutChartOptions = (
  currencyFormat,
  labels,
  colors,
) => {
  const formatSafe = (value) => {
    const n = Number(value)
    return currencyFormat.format(Number.isFinite(n) ? n : 0)
  }
  return {
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 600,
              color: '#3b3e66',
            },
            value: {
              fontSize: '16px',
              fontWeight: 700,
              color: '#070919',
              formatter: formatSafe,
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '14px',
              fontWeight: 600,
              color: '#3b3e66',
              formatter: (value) => {
                const total = value.config.series.reduce(
                  (sum, val) => {
                    const n = Number(val)
                    return sum + (Number.isFinite(n) ? n : 0)
                  },
                  0,
                )
                return formatSafe(total)
              },
            },
          },
        },
      },
    },
    tooltip: {
      fillSeriesColor: false,
      theme: 'dark',
      custom: function ({ series, seriesIndex, w }) {
        const idx = Number.isInteger(seriesIndex) ? seriesIndex : 0
        const color =
          w?.globals?.colors?.[idx] || colors?.[idx] || '#368cff'
        const label =
          w?.globals?.labels?.[idx] || labels?.[idx] || ''
        const value = Array.isArray(series) ? series[idx] : series
        return (
          '<div class="arrow_box hf-chart-tooltip">' +
          `<span class="hf-chart-tooltip-dot" style="background-color:${color}"></span>` +
          `<span class="hf-chart-tooltip-label">${label}: ${formatSafe(value)}</span>` +
          '</div>'
        )
      },
    },
    legend: {
      position: 'bottom',
      show: false,
    },
    colors: colors,
    labels: labels,
  }
}

/**
 * Calculate strategy percentage
 * @param {Object} analytics - Analytics object
 * @param {string} strategyKey - Strategy key (e.g., 'buyHold', 'fixFlip')
 * @param {Object} [fieldKeys] - Optional analytics field names (Wholesale uses *Wholesale suffixes)
 * @param {string} [fieldKeys.belowKey='belowTarget']
 * @param {string} [fieldKeys.onKey='onTarget']
 * @param {string} [fieldKeys.aboveKey='aboveTarget']
 * @returns {number} Strategy percentage
 */
export const calculateStrategyPercentage = (
  analytics,
  strategyKey,
  fieldKeys = {},
) => {
  const strategy = analytics?.[strategyKey]
  if (!strategy) return 0

  const belowKey = fieldKeys.belowKey || 'belowTarget'
  const onKey = fieldKeys.onKey || 'onTarget'
  const aboveKey = fieldKeys.aboveKey || 'aboveTarget'

  const total =
    (Number(strategy[belowKey]) || 0) +
    (Number(strategy[onKey]) || 0) +
    (Number(strategy[aboveKey]) || 0)
  if (total === 0) return 0

  const percentage =
    ((Number(strategy[onKey]) || 0) +
      (Number(strategy[aboveKey]) || 0)) /
    total
  return Number.isFinite(percentage) ? percentage : 0
}

/**
 * Create strategy summary object
 * @param {Object} analytics - Analytics object
 * @param {string} strategyKey - Strategy key
 * @param {string} strategyName - Strategy name
 * @param {Array} analyticsData - Analytics data array
 * @param {Object} [fieldKeys] - Optional analytics field names for percentage calc
 * @returns {Object} Strategy summary
 */
export const createStrategySummary = (
  analytics,
  strategyKey,
  strategyName,
  analyticsData,
  fieldKeys,
) => {
  return {
    name: strategyName,
    percentage: calculateStrategyPercentage(
      analytics,
      strategyKey,
      fieldKeys,
    ),
    analytics: analyticsData,
  }
}
